import React, { useCallback, useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

/**
 * How a member wants to be communicated with — the Accessible Information
 * Standard, via Consent §9.1.
 *
 * "Melio identifies, records, flags and meets a person's communication needs in
 * accordance with the Accessible Information Standard." None of the five steps
 * existed: a search of the whole repo for interpreter, communicationNeed,
 * accessibleFormat, easy read, BSL or reasonable adjustment returned nothing but
 * `aria-label` comments.
 *
 * WORDED AS A CHOICE, NOT A DISCLOSURE. Inclusion §6.12 is explicit that a
 * disability, a sensory impairment, low digital confidence and needing an
 * interpreter are NOT grounds for exclusion — so this must not read like a form
 * that decides whether Melio will take you. It asks what would make things work,
 * and says the adjustments are already available.
 */

const recordFn = httpsCallable<
  { needs: string[]; language?: string; detail?: string },
  { ok: boolean; requiresArrangement: boolean }
>(functions, 'recordCommunicationNeeds');

const OPTIONS: Array<{ key: string; label: string; help?: string }> = [
  { key: 'interpreter_bsl', label: 'A British Sign Language interpreter' },
  {
    key: 'interpreter_spoken',
    label: 'An interpreter for a spoken language',
    help: 'Tell us which language below and we will book a professional interpreter.',
  },
  { key: 'telephone_not_video', label: 'Telephone appointments rather than video' },
  { key: 'written_not_telephone', label: 'Written contact rather than telephone' },
  { key: 'longer_appointments', label: 'Longer appointments' },
  { key: 'supporter_present', label: 'Someone with me on the call' },
  { key: 'large_print', label: 'Large print' },
  { key: 'easy_read', label: 'Easy Read' },
  { key: 'braille', label: 'Braille' },
  { key: 'audio', label: 'Audio' },
  { key: 'lipreading_or_clear_speech', label: 'Clear speech, so I can lip-read' },
  { key: 'other', label: 'Something else' },
];

export default function CommunicationNeeds() {
  const { currentUser } = useAuth();
  const [needs, setNeeds] = useState<string[]>([]);
  const [language, setLanguage] = useState('');
  const [detail, setDetail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!currentUser) return;
    try {
      const snap = await getDoc(
        doc(db, 'users', currentUser.uid, 'communication_needs', 'current'),
      );
      if (snap.exists()) {
        const data = snap.data() as { needs?: string[]; language?: string; detail?: string };
        setNeeds(data.needs || []);
        setLanguage(data.language || '');
        setDetail(data.detail || '');
      }
    } catch {
      setError('Could not load your preferences. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => { void load(); }, [load]);

  const toggle = (key: string) => {
    setSaved(false);
    setNeeds((current) => (current.includes(key)
      ? current.filter((n) => n !== key)
      : [...current, key]));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await recordFn({
        needs,
        language: language || undefined,
        detail: detail || undefined,
      });
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!currentUser) return null;

  return (
    <div>
      <p className="text-sm mb-4" style={{ color: 'var(--melio-text-muted)' }}>
        Tell us what would make appointments and letters work better for you. We arrange these —
        they are not a reason we would turn anyone away, and you can change them whenever you like.
      </p>

      {error && (
        <div
          className="rounded-xl border px-4 py-3 mb-4 text-sm"
          style={{ borderColor: 'rgba(139, 61, 43,0.4)' }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>Loading…</p>
      ) : (
        <div className="space-y-2">
          {OPTIONS.map((option) => (
            <label key={option.key} className="flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={needs.includes(option.key)}
                onChange={() => toggle(option.key)}
              />
              <span>
                <span style={{ color: 'var(--melio-charcoal)' }}>{option.label}</span>
                {option.help && needs.includes(option.key) && (
                  <span className="block text-xs mt-0.5" style={{ color: 'var(--melio-text-muted)' }}>
                    {option.help}
                  </span>
                )}
              </span>
            </label>
          ))}

          {needs.includes('interpreter_spoken') && (
            <label className="block pt-2">
              <span className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>
                Which language?
              </span>
              <input
                value={language}
                onChange={(e) => { setLanguage(e.target.value); setSaved(false); }}
                className="w-full rounded-xl border px-4 py-3 text-sm"
                style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-charcoal)' }}
              />
            </label>
          )}

          {needs.includes('other') && (
            <label className="block pt-2">
              <span className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>
                What would help?
              </span>
              <textarea
                rows={3}
                value={detail}
                onChange={(e) => { setDetail(e.target.value); setSaved(false); }}
                className="w-full rounded-xl border px-4 py-3 text-sm"
                style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-charcoal)' }}
              />
            </label>
          )}

          <div className="flex items-center gap-3 pt-3">
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving}
              className="px-4 py-2.5 rounded-lg text-sm font-medium disabled:opacity-40"
              style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            {saved && (
              <span className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>Saved.</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
